/**
 * Security Configuration
 * 
 * Comprehensive security settings for Content Security Policy,
 * API endpoint whitelisting, and environment-based policies.
 */

export interface SecurityConfig {
  csp: CSPDirectives;
  apiEndpoints: ApiEndpointConfig;
  cors: CorsConfig;
  auth: AuthSecurityConfig;
  headers: SecurityHeaders;
}

export interface CSPDirectives {
  'default-src': string[];
  'script-src': string[];
  'style-src': string[];
  'img-src': string[];
  'font-src': string[];
  'connect-src': string[];
  'media-src': string[];
  'object-src': string[];
  'child-src': string[];
  'worker-src': string[];
  'frame-src': string[];
  'frame-ancestors': string[];
  'form-action': string[];
  'base-uri': string[];
  'manifest-src': string[];
}

export interface ApiEndpointConfig {
  allowedHosts: string[];
  requiredHeaders: string[];
  rateLimit: {
    maxRequests: number;
    windowMs: number;
  };
}

export interface CorsConfig {
  allowedOrigins: string[];
  allowedMethods: string[];
  allowedHeaders: string[];
  credentials: boolean;
}

export interface AuthSecurityConfig {
  sessionTimeout: number;
  tokenRefreshThreshold: number;
  maxLoginAttempts: number;
  lockoutDuration: number;
  requireCSRF: boolean;
}

export interface SecurityHeaders {
  'X-Content-Type-Options': string;
  'X-Frame-Options': string;
  'X-XSS-Protection': string;
  'Referrer-Policy': string;
  'Permissions-Policy': string;
  'Strict-Transport-Security': string;
}

// Environment detection
const isProduction = import.meta.env.PROD;

// Base URLs for different environments
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL || '').trim();
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com';
const FONTS_API_URL = 'https://fonts.googleapis.com';
const FONTS_STATIC_URL = 'https://fonts.gstatic.com';

// Generate nonce for inline scripts/styles
export const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
};

// Current nonce (should be regenerated per request in SSR)
export const currentNonce = generateNonce();

/**
 * Production Security Configuration
 * Strict CSP with minimal unsafe directives
 */
const productionConfig: SecurityConfig = {
  csp: {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      `'nonce-${currentNonce}'`,
      'https://js.stripe.com',
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Required for critical CSS in index.html - charts now use external CSS
      FONTS_API_URL,
      // TODO: Remove 'unsafe-inline' after migrating critical CSS from index.html to external file
    ],
    'img-src': [
      "'self'",
      'data:',
      'https:',
      // Be more specific in production
      'https://*.supabase.co',
    ],
    'font-src': [
      "'self'",
      FONTS_STATIC_URL,
    ],
    'connect-src': [
      "'self'",
      `${SUPABASE_URL}`,
      `wss://${new URL(SUPABASE_URL).hostname}`,
      GEMINI_API_URL,
      'https://api.stripe.com',
    ],
    'media-src': ["'self'"],
    'object-src': ["'none'"],
    'child-src': ["'none'"],
    'worker-src': ["'self'"],
    'frame-src': ["'self'", 'https://js.stripe.com'],
    'frame-ancestors': ["'none'"],
    'form-action': ["'self'"],
    'base-uri': ["'self'"],
    'manifest-src': ["'self'"],
  },
  apiEndpoints: {
    allowedHosts: [
      new URL(SUPABASE_URL).hostname,
      new URL(GEMINI_API_URL).hostname,
    ],
    requiredHeaders: [
      'Authorization',
      'Content-Type',
      'X-Client-Info',
    ],
    rateLimit: {
      maxRequests: 100,
      windowMs: 60 * 1000, // 1 minute
    },
  },
  cors: {
    allowedOrigins: [
      'https://converlang.com',
      'https://www.converlang.com',
      // Add your production domains
    ],
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
    allowedHeaders: [
      'Authorization',
      'Content-Type',
      'X-Client-Info',
      'X-CSRF-Token',
    ],
    credentials: true,
  },
  auth: {
    sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours
    tokenRefreshThreshold: 5 * 60 * 1000, // 5 minutes
    maxLoginAttempts: 5,
    lockoutDuration: 15 * 60 * 1000, // 15 minutes
    requireCSRF: true,
  },
  headers: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(self), geolocation=(), payment=()',
    'Strict-Transport-Security': 'max-age=63072000; includeSubDomains; preload',
  },
};

/**
 * Development Security Configuration
 * More permissive for development workflow
 */
const developmentConfig: SecurityConfig = {
  csp: {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      "'unsafe-inline'", // For Vite HMR
      "'unsafe-eval'", // For Vite dev server
      'localhost:*',
      '127.0.0.1:*',
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // For Vite HMR and development
      FONTS_API_URL,
    ],
    'img-src': [
      "'self'",
      'data:',
      'https:',
      'http://localhost:*',
      'http://127.0.0.1:*',
    ],
    'font-src': [
      "'self'",
      FONTS_STATIC_URL,
    ],
    'connect-src': [
      "'self'",
      `${SUPABASE_URL}`,
      `wss://${new URL(SUPABASE_URL).hostname}`,
      GEMINI_API_URL,
      'ws://localhost:*',
      'ws://127.0.0.1:*',
      'http://localhost:*',
      'http://127.0.0.1:*',
    ],
    'media-src': ["'self'"],
    'object-src': ["'none'"],
    'child-src': ["'self'"],
    'worker-src': ["'self'", 'blob:'],
    'frame-src': ["'self'"],
    'frame-ancestors': ["'self'"],
    'form-action': ["'self'"],
    'base-uri': ["'self'"],
    'manifest-src': ["'self'"],
  },
  apiEndpoints: {
    allowedHosts: [
      'localhost',
      '127.0.0.1',
      new URL(SUPABASE_URL).hostname,
      new URL(GEMINI_API_URL).hostname,
    ],
    requiredHeaders: [
      'Content-Type',
    ],
    rateLimit: {
      maxRequests: 1000,
      windowMs: 60 * 1000, // 1 minute
    },
  },
  cors: {
    allowedOrigins: [
      'http://localhost:3000',
      'http://127.0.0.1:3000',
      'http://localhost:5173',
      'http://127.0.0.1:5173',
    ],
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
    allowedHeaders: [
      'Authorization',
      'Content-Type',
      'X-Client-Info',
    ],
    credentials: true,
  },
  auth: {
    sessionTimeout: 7 * 24 * 60 * 60 * 1000, // 7 days for development
    tokenRefreshThreshold: 5 * 60 * 1000, // 5 minutes
    maxLoginAttempts: 10,
    lockoutDuration: 5 * 60 * 1000, // 5 minutes
    requireCSRF: false, // Disabled for development ease
  },
  headers: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'SAMEORIGIN',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'no-referrer-when-downgrade',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
    'Strict-Transport-Security': '', // Disabled for development
  },
};

/**
 * Get security configuration based on environment
 */
export const getSecurityConfig = (): SecurityConfig => {
  return isProduction ? productionConfig : developmentConfig;
};

/**
 * Convert CSP object to string
 */
export const generateCSPString = (csp: CSPDirectives): string => {
  return Object.entries(csp)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
};

/**
 * Validate if a URL is allowed by CSP
 */
export const isAllowedByCSP = (url: string, directive: keyof CSPDirectives): boolean => {
  const config = getSecurityConfig();
  const sources = config.csp[directive];
  
  if (sources.includes("'self'") && url.startsWith(window.location.origin)) {
    return true;
  }
  
  if (sources.includes('https:') && url.startsWith('https:')) {
    return true;
  }
  
  return sources.some(source => {
    if (source.startsWith('https://')) {
      return url.startsWith(source);
    }
    return false;
  });
};

/**
 * Validate API endpoint
 */
export const isAllowedAPIEndpoint = (url: string): boolean => {
  const config = getSecurityConfig();
  const hostname = new URL(url).hostname;
  
  return config.apiEndpoints.allowedHosts.some(allowedHost => {
    if (allowedHost.includes('*')) {
      const pattern = allowedHost.replace('*', '.*');
      return new RegExp(pattern).test(hostname);
    }
    return hostname === allowedHost;
  });
};

/**
 * Get required headers for API calls
 */
export const getRequiredHeaders = (): Record<string, string> => {
  const config = getSecurityConfig();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client-Info': 'converlang-web',
  };
  
  if (config.auth.requireCSRF) {
    headers['X-CSRF-Token'] = generateCSRFToken();
  }
  
  return headers;
};

/**
 * Generate CSRF token
 */
export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
};

/**
 * Security event logging
 */
export const logSecurityEvent = (event: string, details: Record<string, any>) => {
  if (isProduction) {
    // In production, send to security monitoring service
    console.warn(`[SECURITY] ${event}:`, details);
    // TODO: Implement proper security logging service
  } else {
    console.log(`[SECURITY] ${event}:`, details);
  }
};

export default getSecurityConfig();